import Link from 'next/link'

import clsx from 'clsx'

import type { ComponentProps, JSX } from 'react'

const focusRing = 'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'

const gridClasses = {
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-3',
  names: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4',
  states: 'grid-cols-5 sm:grid-cols-8 lg:grid-cols-10',
}

type LinkGridProps = Omit<ComponentProps<'section'>, 'children' | 'title'> & {
  title?: string
  links: { label: string; href: string }[]
  /** `2` and `3` are one column at phone width; `names` and `states` scale up to four and ten columns. */
  columns?: keyof typeof gridClasses
  titleAs?: 'h2' | 'h3'
}

export default function LinkGrid({
  title,
  titleAs: Title = 'h3',
  links,
  columns = 3,
  className,
  ...rest
}: LinkGridProps): JSX.Element {
  return (
    <section {...rest} className={clsx('flex flex-col gap-4 font-sans', className)}>
      {title && <Title className="text-xl font-medium leading-tight text-ink">{title}</Title>}
      <ul className={`grid gap-x-6 gap-y-2 ${gridClasses[columns]}`}>
        {links.map((link, index) => (
          // Neither href nor label is unique: "All Barista jobs" and "Barista" share a link, Wellness lists one role twice.
          <li key={index}>
            <Link
              href={link.href}
              prefetch={false}
              className={`inline-flex min-h-11 items-center text-base text-ink underline ${focusRing}`}
            >
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  )
}
